import {
  MysteryShopRequestGroup,
  MysteryShopRequestOutbound,
  MysteryShopRequestsBatch,
  ShopperContactOwnerType,
  ShopperEmailType,
  ShopRequestShopperEmailType,
  ShopRequestTranscriptRequirementActionPayload,
  ShopRequestTranscriptRequirementType,
} from 'components/MysteryShopping/types';
import { getShopperEmailMailboxSyncRetryApiPath } from 'constants/actionCenter';

import axios from '../config/axiosPrivate';

export const getShopRequests = async (params: any, signal?: AbortSignal) => {
  const url = '/shop_requests/';
  const response = await axios.get(url, {
    params,
    signal,
  });
  return response.data;
};

export const getShopRequestGroups = async (params: any) => {
  const url = '/shop_requests/groups';
  const response = await axios.get(url, { params });
  return response.data;
};

export const createShopRequests = async (payload: MysteryShopRequestGroup) => {
  const url = '/shop_requests/create';
  const response = await axios.post(url, payload);
  return response.data;
};

export const updateShopRequest = async (id: number, payload: Partial<MysteryShopRequestOutbound>) => {
  const url = `/shop_requests/${id}`;
  const response = await axios.patch(url, payload);
  return response.data;
};

export const validateShopRequest = async (id: number) => {
  const url = `/shop_requests/${id}/validate`;
  const response = await axios.get(url);
  const data = response.data;
  return data?.warnings ?? [];
};

export const updateShopRequestTranscriptRequirement = async (
  id: number,
  payload: ShopRequestTranscriptRequirementActionPayload
): Promise<ShopRequestTranscriptRequirementType> => {
  const url = `/shop_requests/${id}/transcript-requirement`;
  const response = await axios.patch(url, payload);
  return response.data;
};

export const getShopRequestTranscriptRequirement = async (
  id: number,
  shopperId?: number | null
): Promise<ShopRequestTranscriptRequirementType> => {
  const url = `/shop_requests/${id}/transcript-requirement`;
  const response = await axios.get(url, {
    params: {
      shopper_id: shopperId,
    },
  });
  return response.data;
};

export const batchUpdateShopRequests = async (payload: MysteryShopRequestsBatch) => {
  const url = `/shop_requests/batch-update`;
  const response = await axios.patch(url, payload);
  return response.data;
};

export const batchDeleteShopRequests = async (payload: any) => {
  const url = `/shop_requests/batch-delete`;
  const response = await axios.post(url, payload);
  return response.data;
};

export const getShopRequestStats = async (params: any) => {
  const url = `/shop_requests/metrics`;
  const response = await axios.get(url, { params });
  return response.data;
};

export const deleteShopRequest = async (id: number) => {
  const url = `/shop_requests/${id}`;
  const response = await axios.delete(url);
  return response.data;
};

export const fetchShopRequestTabsMetrics = async (params: any) => {
  const url = '/shop_requests/tabs_metrics';
  const response = await axios.get(url, { params });
  return response.data;
};

export const getShopRequestShopperEmails = async (shopRequestId: number) => {
  const url = `/shop_requests/${shopRequestId}/shopper-emails`;
  const response = await axios.get(url);
  return response.data;
};

export const getShopperEmails = async (
  params: {
    page?: number;
    page_size?: number;
    search?: string;
    ordering?: string;
    mailbox_status?: string[];
    linked_shop_requests?: string[];
    label_presence?: string[];
    owner_id?: number[];
  } = {}
) => {
  const url = '/shop_requests/shopper-emails';
  const response = await axios.get(url, {
    params,
    paramsSerializer: {
      indexes: null,
    },
  });
  return response.data;
};

export const getShopperEmailOwners = async (): Promise<ShopperContactOwnerType[]> => {
  const url = '/shop_requests/shopper-emails/owners';
  const response = await axios.get(url);
  return response.data;
};

export const getShopperEmail = async (shopperEmailId: number) => {
  const url = `/shop_requests/shopper-emails/${shopperEmailId}`;
  const response = await axios.get(url);
  return response.data;
};

export const createShopperEmail = async (payload: Pick<ShopperEmailType, 'name' | 'email' | 'phone'>) => {
  const url = '/shop_requests/shopper-emails';
  const response = await axios.post(url, payload);
  return response.data;
};

export const updateShopperEmail = async (
  shopperEmailId: number,
  payload: Pick<ShopperEmailType, 'name' | 'email' | 'phone'>
) => {
  const url = `/shop_requests/shopper-emails/${shopperEmailId}`;
  const response = await axios.patch(url, payload);
  return response.data;
};

export const deleteShopperEmail = async (shopperEmailId: number) => {
  const url = `/shop_requests/shopper-emails/${shopperEmailId}`;
  const response = await axios.delete(url);
  return response.data;
};

export const authenticateShopperEmailMailbox = async (shopperEmailId: number) => {
  const url = `/shop_requests/shopper-emails/${shopperEmailId}/authenticate-mailbox`;
  const response = await axios.post(url);
  return response.data;
};

export const retryShopperEmailMailboxSync = async (shopperEmailId: number) => {
  const url = getShopperEmailMailboxSyncRetryApiPath(shopperEmailId);
  const response = await axios.post(url);
  return response.data;
};

export const authenticateShopRequestShopperEmailMailbox = async (shopRequestId: number, shopperEmailId: number) => {
  const url = `/shop_requests/${shopRequestId}/shopper-emails/${shopperEmailId}/authenticate-mailbox`;
  const response = await axios.post(url);
  return response.data;
};

export const unlinkShopperEmailMailbox = async (shopperEmailId: number) => {
  const url = `/shop_requests/shopper-emails/${shopperEmailId}/unlink-mailbox`;
  const response = await axios.post(url);
  return response.data;
};

export const createShopRequestShopperEmail = async (shopRequestId: number, payload: ShopRequestShopperEmailType) => {
  const url = `/shop_requests/${shopRequestId}/shopper-emails`;
  const response = await axios.post(url, payload);
  return response.data;
};

export const updateShopRequestShopperEmail = async (
  shopRequestId: number,
  shopperEmailId: number,
  payload: ShopRequestShopperEmailType
) => {
  const url = `/shop_requests/${shopRequestId}/shopper-emails/${shopperEmailId}`;
  const response = await axios.patch(url, payload);
  return response.data;
};

export const deleteShopRequestShopperEmail = async (shopRequestId: number, shopperEmailId: number) => {
  const url = `/shop_requests/${shopRequestId}/shopper-emails/${shopperEmailId}`;
  const response = await axios.delete(url);
  return response.data;
};

export const fetchShopRequestShopperEmailMailboxMessages = async (shopRequestId: number, shopperEmailId: number) => {
  const url = `/shop_requests/${shopRequestId}/shopper-emails/${shopperEmailId}/fetch-mailbox-messages`;
  const response = await axios.post(url);
  return response.data;
};

export const getShopRequestCommunityPropagationCandidates = async (
  communityId: number,
  date: Date,
  baseCommunityId?: number
) => {
  const url = `/shop_requests/propagation-candidates/${communityId}`;
  const response = await axios.get(url, { params: { date, base_community_id: baseCommunityId } });
  return response.data;
};
